/**
 * MobileMeteoLite — consulta meteorológica simplificada para el perito en
 * móvil. Sólo lo esencial:
 *   - Dirección (texto) + fecha (default: hoy).
 *   - Resultado: dos cards grandes con VIENTO MEDIO/RÁFAGA y PRECIPITACIÓN
 *     acumulada del día. Resaltado en rojo si superan los umbrales del
 *     informe (≥80 km/h viento, ≥40 mm lluvia).
 *
 * Reusa el endpoint `GET /api/meteo/lookup` ya existente (AEMET). No
 * descarga PDFs ni mapas de rayos — para eso el perito usa la web.
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  MagnifyingGlass, Wind, CloudRain, Thermometer, Warning,
} from "@phosphor-icons/react";
import MobileShell from "@/mobile/MobileShell";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function MobileMeteoLite() {
  const [address, setAddress] = useState("");
  const [date, setDate] = useState(todayISO());
  const [endDate, setEndDate] = useState("");
  const [rangeMode, setRangeMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const search = async () => {
    if (!address.trim()) {
      toast.error("Indica la dirección");
      return;
    }
    if (rangeMode) {
      if (!endDate) { toast.error("Selecciona la fecha final"); return; }
      if (endDate < date) { toast.error("La fecha final debe ser posterior"); return; }
      // Limitamos a 10 días naturales (igual que la web)
      const d1 = new Date(date), d2 = new Date(endDate);
      const days = Math.round((d2 - d1) / 86400000) + 1;
      if (days > 10) { toast.error("Máximo 10 días"); return; }
    }
    setLoading(true);
    try {
      const params = { address: address.trim(), date };
      if (rangeMode && endDate) params.end_date = endDate;
      const r = await api.get("/meteo/lookup", { params });
      setData(r.data);
    } catch (e) {
      setData(null);
      toast.error(e?.response?.data?.detail || "No se pudieron obtener datos");
    } finally { setLoading(false); }
  };

  const weather = data?.weather;
  // El backend devuelve `weather.days[]` cuando is_range = true. Para un único
  // día, devuelve los campos directamente bajo `weather`.
  const isRange = !!weather?.range;
  const days = weather?.days || [];
  const day = isRange ? null : weather;
  const windMax = day?.rafaga_max_kmh ?? day?.viento_max_kmh;
  const windMean = day?.viento_max_kmh;
  const precip = day?.precipitacion_mm;
  const tMax = day?.temperatura_max;
  const tMin = day?.temperatura_min;

  const windAlert = !isRange && windMax >= 80;
  const precipAlert = !isRange && precip >= 40;

  return (
    <MobileShell title="Meteo">
      <div className="px-3 py-3 space-y-2">
        <input
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search()}
          placeholder="Dirección, municipio, provincia…"
          data-testid="meteo-lite-address"
          className="w-full text-[14px] !h-11"
          inputMode="text"
          autoCorrect="off"
        />
        {/* Toggle Día único / Rango */}
        <div className="flex items-center gap-1 bg-zinc-100 rounded-md p-1 text-[12px]">
          <button
            onClick={() => { setRangeMode(false); setEndDate(""); }}
            data-testid="meteo-lite-mode-single"
            className={`flex-1 py-1.5 rounded-md font-semibold transition ${
              !rangeMode ? "bg-white text-foreground shadow-sm" : "text-zinc-600"}`}>
            Día único
          </button>
          <button
            onClick={() => setRangeMode(true)}
            data-testid="meteo-lite-mode-range"
            className={`flex-1 py-1.5 rounded-md font-semibold transition ${
              rangeMode ? "bg-white text-foreground shadow-sm" : "text-zinc-600"}`}>
            Rango (máx 10 días)
          </button>
        </div>
        <div className={`grid gap-2 ${rangeMode ? "grid-cols-2" : "grid-cols-[1fr,auto]"}`}>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="meteo-lite-date"
            max={todayISO()}
            className="text-[14px] !h-11"
          />
          {rangeMode ? (
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              data-testid="meteo-lite-end-date"
              max={todayISO()}
              min={date}
              className="text-[14px] !h-11"
              placeholder="Hasta"
            />
          ) : (
            <button
              onClick={search}
              disabled={loading}
              data-testid="meteo-lite-search"
              className="bg-foreground text-white text-[13px] font-semibold px-4 h-11 rounded-md inline-flex items-center gap-1.5 disabled:opacity-50">
              <MagnifyingGlass className="w-4 h-4" weight="bold" />
              {loading ? "…" : "Consultar"}
            </button>
          )}
        </div>
        {rangeMode && (
          <button
            onClick={search}
            disabled={loading}
            data-testid="meteo-lite-search-range"
            className="w-full bg-foreground text-white text-[13px] font-semibold px-4 h-11 rounded-md inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
            <MagnifyingGlass className="w-4 h-4" weight="bold" />
            {loading ? "Consultando…" : "Consultar rango"}
          </button>
        )}
        {data?.address_resolved && (
          <p className="text-[11px] text-muted">
            📍 {data.address_resolved}
          </p>
        )}
      </div>

      {weather && !weather.available && (
        <div className="mx-3 bg-amber-50 border border-amber-200 rounded-md p-3 text-[12px] text-amber-900"
             data-testid="meteo-lite-noavail">
          <Warning className="w-4 h-4 inline mr-1" weight="fill" />
          Sin datos para esta fecha. {weather.error || ""}
        </div>
      )}

      {day && weather?.available && !isRange && (
        <div className="mx-3 space-y-2" data-testid="meteo-lite-result">
          <MetricCard
            icon={Wind}
            color="emerald"
            alert={windAlert}
            label="Viento"
            primary={windMax != null ? `${Math.round(windMax)} km/h` : "—"}
            secondary={windMean != null && windMean !== windMax ? `viento medio ${Math.round(windMean)} km/h` : "ráfaga máxima del día"}
            note={windAlert ? "⚠ Ráfagas ≥ 80 km/h" : null}
          />
          <MetricCard
            icon={CloudRain}
            color="blue"
            alert={precipAlert}
            label="Precipitación"
            primary={precip != null ? `${precip.toFixed(1)} mm` : "—"}
            secondary={precip != null ? "acumulado del día" : null}
            note={precipAlert ? "⚠ ≥ 40 mm en 24 h" : null}
          />
          <MetricCard
            icon={Thermometer}
            color="orange"
            label="Temperatura"
            primary={tMax != null ? `${Math.round(tMax)}°` : "—"}
            secondary={tMin != null ? `mín ${Math.round(tMin)}°` : null}
          />
          <p className="text-[10.5px] text-muted text-center pt-2">
            Datos AEMET · Para el informe PDF completo entra desde la web.
          </p>
        </div>
      )}

      {/* Vista de rango: una fila compacta por día */}
      {isRange && weather?.available && days.length > 0 && (
        <RangeView days={days} />
      )}
    </MobileShell>
  );
}

function RangeView({ days }) {
  return (
    <div className="mx-3" data-testid="meteo-lite-range">
      <div className="text-[11px] text-muted mb-2">
        {days.length} día{days.length === 1 ? "" : "s"} · Pulsa una fila para verla en detalle
      </div>
      <div className="bg-white border border-[#E8EAED] rounded-md overflow-hidden">
        <div className="grid grid-cols-[1fr,auto,auto] gap-2 px-2 py-1.5 bg-zinc-50 text-[10.5px] font-semibold uppercase tracking-wide text-muted">
          <div>Día</div>
          <div className="text-right">Viento</div>
          <div className="text-right">Lluvia</div>
        </div>
        {days.map((d) => {
          const wind = d.rafaga_max_kmh ?? d.viento_max_kmh;
          const precip = d.precipitacion_mm;
          const wAlert = wind >= 80;
          const pAlert = precip >= 40;
          return (
            <div key={d.date}
                 className="grid grid-cols-[1fr,auto,auto] gap-2 px-2 py-2 border-t border-[#E8EAED] text-[13px] items-center"
                 data-testid={`meteo-lite-day-${d.date}`}>
              <div>
                <div className="font-semibold">{formatDay(d.date)}</div>
                {d.temperatura_max != null && (
                  <div className="text-[10.5px] text-muted">
                    {Math.round(d.temperatura_min ?? 0)}° – {Math.round(d.temperatura_max)}°
                  </div>
                )}
              </div>
              <div className={`text-right tabular-nums font-semibold ${wAlert ? "text-rose-700" : "text-foreground"}`}>
                {wind != null ? `${Math.round(wind)}` : "—"}
                <span className="text-[10px] text-muted ml-0.5">km/h</span>
                {wAlert && <div className="text-[9px] text-rose-700 leading-none">⚠ ráfaga</div>}
              </div>
              <div className={`text-right tabular-nums font-semibold ${pAlert ? "text-rose-700" : "text-foreground"}`}>
                {precip != null ? precip.toFixed(1) : "—"}
                <span className="text-[10px] text-muted ml-0.5">mm</span>
                {pAlert && <div className="text-[9px] text-rose-700 leading-none">⚠ &gt; 40</div>}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[10.5px] text-muted text-center mt-3">
        Datos AEMET · Para el PDF completo entra desde la web.
      </p>
    </div>
  );
}

function formatDay(iso) {
  const d = new Date(iso + "T00:00");
  return d.toLocaleDateString("es-ES", { weekday: "short", day: "2-digit", month: "short" });
}

function MetricCard({ icon: Icon, color = "zinc", alert, label, primary, secondary, note }) {
  const bg = alert ? "bg-rose-50 border-rose-300" : "bg-white border-[#E8EAED]";
  const tint = alert ? "text-rose-700" : {
    emerald: "text-emerald-600",
    blue: "text-blue-600",
    orange: "text-orange-600",
    zinc: "text-zinc-600",
  }[color] || "text-zinc-600";
  return (
    <div className={`border rounded-md p-3 flex items-center gap-3 ${bg}`}
         data-testid={`meteo-lite-card-${label.toLowerCase()}`}>
      <div className={`w-10 h-10 rounded-md flex items-center justify-center bg-white/60 ${tint}`}>
        <Icon className="w-5 h-5" weight="duotone" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-wide font-semibold text-muted">{label}</div>
        <div className={`text-[22px] font-bold leading-tight tabular-nums ${alert ? "text-rose-700" : "text-foreground"}`}>
          {primary}
        </div>
        {secondary && <div className="text-[11px] text-muted">{secondary}</div>}
        {note && <div className="text-[11px] font-semibold text-rose-700 mt-0.5">{note}</div>}
      </div>
    </div>
  );
}
